import React, { createContext, useState, useContext, useEffect } from "react";
import JwtUtils from "../utils/JWtUtils";
import { useLogin, useForgotPassword, useVerifyResetCode, useResetPassword, useChangePassword } from "../hooks";
import { Dialog } from "@mui/material";
import LoginForm from "../../modules/auth/components/LoginForm";
import { MODE_LOGIN } from "../types/app";
import InscriptionDialogContent from "../../modules/auth/components/InscriptionDialogContent";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem("authToken");
    return token && JwtUtils.isTokenValidAndNotExpired();
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [mode, setMode] = useState(MODE_LOGIN.LOGIN);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(() => {
    return localStorage.getItem("needsPasswordChange") === "true";
  });

  const { mutate: doLogin, isPending } = useLogin();
  const { mutate: doForgotPassword } = useForgotPassword();
  const { mutate: doVerifyResetCode } = useVerifyResetCode();
  const { mutate: doResetPassword } = useResetPassword();

  const handleLoginOpen = () => setIsLoginOpen(true);
  const handleLoginClose = () => setOpenAuthDialog(false);
  
  const login = (credentials, captchaResponse) => {
    setIsLoading(true);
    setError(null);

    return new Promise((resolve) => {
      doLogin({ ...credentials, captchaResponse }, {
        onSuccess: (data) => {
          setIsAuthenticated(true);         
          if (data.passwordChanged === false) {
            localStorage.setItem("needsPasswordChange", "true");
            setNeedsPasswordChange(true);
          } else {
            localStorage.removeItem("needsPasswordChange");
            setNeedsPasswordChange(false);
          }         
          setIsLoading(false);
          setError(null);
          window.location.reload();
          resolve(true);
        },
        onError: (errorResponse) => {
          const errorMessage = errorResponse?.response?.data?.message ||
            errorResponse.message ||
            "Nom d'utilisateur ou mot de passe incorrect";

          setError(errorMessage);
          setIsLoading(false);
          resolve(false);
        }
      });
    });
  };

  const forgotPassword = async (email) => {
    setIsLoading(true);
    setResetEmail(email);

    return new Promise((resolve, reject) => {
      doForgotPassword(
        { email },
        {
          onSuccess: () => {
            setIsLoading(false);
            resolve(true);
          },
          onError: (errorResponse) => {
            setError(errorResponse?.response?.data?.message || "Email not found");
            setIsLoading(false);
            reject(errorResponse);
          }
        }
      );
    });
  };

  const verifyResetCode = (email, verificationCode) => {
    setIsLoading(true);

    return new Promise((resolve, reject) => {
      doVerifyResetCode({ email, verificationCode }, {
        onSuccess: () => {
          setIsLoading(false);
          resolve(true);
        },
        onError: (errorResponse) => {
          setError(errorResponse.message || "Invalid reset code");
          setIsLoading(false);
          reject(errorResponse);
        }
      });
    });
  };

  const resetPassword = (newPassword, confirmPassword) => {
    setIsLoading(true);

    return new Promise((resolve, reject) => {
      doResetPassword({ email: resetEmail, newPassword, confirmPassword }, {
        onSuccess: () => {
          setIsLoading(false);
          resolve(true);
        },
        onError: (errorResponse) => {
          setError(errorResponse.message || "Password reset failed");
          setIsLoading(false);
          reject(errorResponse);
        }
      });
    });
  };

  const toggleLoginDialog = () => {
    setMode(MODE_LOGIN.LOGIN);
    setOpenAuthDialog(true);
  };

  const toggleInscriptionDialog = () => {
    setMode(MODE_LOGIN.INSCRIPTION);
    setOpenAuthDialog(true);
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("needsPasswordChange");
    setIsAuthenticated(false);
    setNeedsPasswordChange(false);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        login, 
        logout, 
        error, 
        handleLoginOpen, 
        forgotPassword, 
        toggleLoginDialog, 
        toggleInscriptionDialog, 
        verifyResetCode, 
        resetPassword, 
        setIsLoginOpen,
        needsPasswordChange,
        setShowChangePasswordDialog,
        setNeedsPasswordChange,
        showChangePasswordDialog
      }}
    >
      {children}
      <Dialog open={openAuthDialog} onClose={handleLoginClose} fullWidth maxWidth="xs">
        <AuthDialogContent mode={mode} onClose={handleLoginClose} login={login} error={error} />
      </Dialog>
    </AuthContext.Provider>
  );
};

const AuthDialogContent = (props) => {
  const { mode } = props;
  switch (mode) {
    case MODE_LOGIN.LOGIN:
      return <LoginForm {...props} />;
    case MODE_LOGIN.INSCRIPTION:
      return <InscriptionDialogContent {...props} />;
    default:
      return <LoginForm {...props} />;
  }
};

export const useAuth = () => useContext(AuthContext);